import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

/**
 * Hooks for the Services Framework delivery plan.
 *
 * The framework owns a baseline weekly plan that Service Packages and
 * Projects inherit from. These hooks hit /admin/framework/delivery-plan
 * and the parallel /versions routes.
 */

// ============ Types ============

export interface FrameworkAllocation {
  activityId: string;
  week: number;
  days: number; // centdays (1.5 days = 150)
}

export interface FrameworkActivityBaseDays {
  activityId: string;
  baseDays: number; // centdays
}

export interface FrameworkCapabilityStartWeek {
  capabilityId: string;
  startWeek: number;
}

export interface FrameworkDeliveryPlanResponse {
  allocations: FrameworkAllocation[];
  activities: FrameworkActivityBaseDays[];
  capabilityStartWeeks: FrameworkCapabilityStartWeek[];
  version: number;
}

export interface FrameworkPlanVersion {
  id: string;
  version: number;
  changeDescription: string | null;
  changedFields: string[] | null;
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

interface FrameworkPlanVersionsResponse {
  versions: FrameworkPlanVersion[];
}

interface SaveFrameworkPlanInput {
  // Keyed by week number (string), values in decimal days. Same shape
  // as the profile save payload.
  allocations: Array<{
    activityId: string;
    weekAllocations: Record<string, number>;
  }>;
  // Optional: override framework base days in centdays. When omitted,
  // vcf_activities.base_days is left untouched.
  activities?: Array<{
    activityId: string;
    baseDays: number;
  }>;
  // Optional: framework default start week per capability.
  capabilityStartWeeks?: Array<{
    capabilityId: string;
    startWeek: number;
  }>;
  changeDescription?: string;
}

interface SaveFrameworkPlanResponse {
  success: boolean;
  version: number;
  savedAt: string;
}

interface RestoreVersionResponse {
  success: boolean;
  newVersion: number;
  restoredFromVersion: number;
}

// ============ Query Hooks ============

export function useFrameworkDeliveryPlan() {
  return useQuery({
    queryKey: ['framework-delivery-plan'],
    queryFn: () =>
      api.get<FrameworkDeliveryPlanResponse>('/admin/framework/delivery-plan'),
    staleTime: 30 * 1000,
  });
}

export function useFrameworkPlanVersions() {
  return useQuery({
    queryKey: ['framework-delivery-plan', 'versions'],
    queryFn: () =>
      api.get<FrameworkPlanVersionsResponse>(
        '/admin/framework/delivery-plan/versions'
      ),
    staleTime: 30 * 1000,
  });
}

// ============ Mutation Hooks ============

export function useSaveFrameworkDeliveryPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveFrameworkPlanInput) =>
      api.put<SaveFrameworkPlanResponse>(
        '/admin/framework/delivery-plan',
        input
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-delivery-plan'] });
      // Invalidate anything that consumes the effective plan too, because
      // the framework baseline is a dependency.
      queryClient.invalidateQueries({ queryKey: ['effective-delivery-plan'] });
      // Profile plans carry a `framework` baseline in their response, so
      // editing the framework should refresh open profile views.
      queryClient.invalidateQueries({ queryKey: ['delivery-profiles'] });
    },
  });
}

export function useRestoreFrameworkPlanVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (versionId: string) =>
      api.post<RestoreVersionResponse>(
        `/admin/framework/delivery-plan/versions/${versionId}/restore`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['framework-delivery-plan'] });
      queryClient.invalidateQueries({ queryKey: ['effective-delivery-plan'] });
      queryClient.invalidateQueries({ queryKey: ['delivery-profiles'] });
    },
  });
}
